import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { createScheduledService, getPreferredDrivers } from '../services/deliveryServices';
import Swal from 'sweetalert2';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'No repetir' },
  { value: 'daily', label: 'Diario' },
  { value: 'weekdays', label: 'Lunes a Viernes' },
  { value: 'weekly', label: 'Semanal' },
];

export function ScheduleRideModal({ show, onHide, passengerUid, isVip, pickupAddress, pickupLat, pickupLng, dropoffAddress, dropoffLat, dropoffLng, referencePoint, serviceType }) {
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [endDate, setEndDate] = useState('');
  const [preferredDriver, setPreferredDriver] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && isVip) {
      const loadDrivers = async () => {
        try {
          const list = await getPreferredDrivers(passengerUid);
          setDrivers(list);
        } catch {
          setDrivers([]);
        }
      };
      loadDrivers();
    }
  }, [show, isVip, passengerUid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createScheduledService({
        passengerUid,
        pickupTime: new Date(`${pickupDate}T${pickupTime}`).toISOString(),
        recurrence,
        endDate: endDate || null,
        pickupAddress,
        pickupLat,
        pickupLng,
        dropoffAddress,
        dropoffLat,
        dropoffLng,
        referencePoint: referencePoint || null,
        type: serviceType || 'ride',
        preferredDriverUid: preferredDriver || null,
        isVip,
      });
      
      Swal.fire({
        icon: 'success',
        title: '¡Viaje Programado!',
        text: 'Tu viaje ha sido programado exitosamente',
        confirmButtonColor: '#0EABB0'
      });
      
      onHide();
    } catch (err) {
      console.error('Error scheduling ride:', err);
      Swal.fire('Error', 'No se pudo programar el viaje. Intenta nuevamente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div 
          className="fixed inset-0 z-[1100] flex flex-col justify-end bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if(e.target === e.currentTarget) onHide(); }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div 
            className="w-full rounded-t-3xl shadow-2xl flex flex-col"
            style={{ maxHeight: '90vh', background: 'var(--bry-bg-card, #FFFFFF)', color: 'var(--bry-text-primary, #0F172A)' }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div className="pt-3 pb-1 flex justify-center cursor-pointer" onClick={onHide}>
              <div className="w-12 h-1.5 rounded-full" style={{ background: '#CBD5E1' }} />
            </div>

            <div className="px-6 py-3 flex items-center justify-between border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="text-xl font-bold m-0" style={{ color: '#0F172A' }}>Programar viaje</h3>
              <button 
                onClick={onHide}
                className="p-2 rounded-full border-0 bg-transparent text-muted fs-4 leading-none"
                style={{ cursor: 'pointer' }}
                aria-label="Cerrar"
              >
                &times;
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 min-h-0 p-6 pb-safe">
              <Form id="schedule-ride-form" onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-muted mb-1">Fecha del viaje</Form.Label>
                  <Form.Control
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="rounded-xl py-3 border fw-semibold"
                    required
                    min={new Date().toISOString().split('T')[0]}
                    style={{ background: '#F8FAFC', color: '#0F172A', borderColor: '#CBD5E1' }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-muted mb-1">Hora de recogida</Form.Label>
                  <Form.Control
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="rounded-xl py-3 border fw-semibold"
                    required
                    style={{ background: '#F8FAFC', color: '#0F172A', borderColor: '#CBD5E1' }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-muted mb-1">Repetir</Form.Label>
                  <Form.Select 
                    className="rounded-xl py-3 border fw-semibold" 
                    value={recurrence} 
                    onChange={(e) => setRecurrence(e.target.value)}
                    style={{ background: '#F8FAFC', color: '#0F172A', borderColor: '#CBD5E1' }}
                  >
                    {RECURRENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {recurrence !== 'none' && (
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-muted mb-1">Hasta (opcional)</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="rounded-xl py-3 border fw-semibold"
                      min={pickupDate || new Date().toISOString().split('T')[0]}
                      style={{ background: '#F8FAFC', color: '#0F172A', borderColor: '#CBD5E1' }}
                    />
                  </Form.Group>
                )}

                {isVip && drivers.length > 0 && (
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-muted mb-1">Conductor preferido</Form.Label>
                    <Form.Select 
                      className="rounded-xl py-3 border fw-semibold" 
                      value={preferredDriver} 
                      onChange={(e) => setPreferredDriver(e.target.value)}
                      style={{ background: '#F8FAFC', color: '#0F172A', borderColor: '#CBD5E1' }}
                    >
                      <option value="">Cualquier conductor</option>
                      {drivers.map((d) => (
                        <option key={d.driver_uid} value={d.driver_uid}>
                          {d.name} {d.is_online ? '(En línea)' : '(Desconectado)'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

              </Form>
            </div>

            {/* ── PIE FIJO: botones siempre visibles, fuera del scroll ── */}
            <div
              className="d-flex gap-3 px-6 py-4 border-top"
              style={{
                flexShrink: 0,
                background: 'var(--bry-bg-card, #FFFFFF)',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <Button
                variant="light"
                className="flex-1 rounded-xl py-3 fw-bold border-0"
                onClick={onHide}
                style={{ background: '#F1F5F9', color: '#475569' }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                form="schedule-ride-form"
                disabled={saving}
                className="flex-1 rounded-xl py-3 fw-bold border-0 shadow-sm"
                style={{ background: '#0EABB0', color: '#FFFFFF', minHeight: 52 }}
              >
                {saving ? 'Guardando...' : 'Programar viaje'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
